import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ConfidenceBand } from './ConfidenceBand';
import type { Confidence } from '@/types';

/* ----------------------------------------------------------------------------
 * Insight / recommendation card — the tri-state AI system made concrete
 * (v1.1 §6.4). Advisory content carries the Signal-Copper keyline + wash + a
 * tracked-mono "✧ proposed by copilot" caption; on human accept it *cools down*
 * to a calm neutral "confirmed by [user]" — the visual proof of "AI proposes,
 * human decides". The colour cool-down animates on the calibration ease (§2.6).
 * -------------------------------------------------------------------------- */
export function InsightCard({
  title,
  children,
  confidence,
  confirmedBy,
  confirmedAt,
  actions,
  className,
}: {
  title: string;
  children: ReactNode;
  confidence?: Confidence;
  /** when set, the insight has been accepted by a human → cooled neutral styling. */
  confirmedBy?: string;
  confirmedAt?: string;
  actions?: ReactNode;
  className?: string;
}) {
  const advisory = !confirmedBy;
  return (
    <div
      className={cn(
        'rounded-md border p-4 transition-[background-color,border-color] duration-normal ease-calibration',
        advisory
          ? 'border-l-2 border-l-ai border-y-border-subtle border-r-border-subtle bg-ai-wash/40'
          : 'border-border-subtle bg-surface',
        className,
      )}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        {advisory ? (
          <div className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
            <span aria-hidden>✧</span> Proposed by copilot
          </div>
        ) : (
          <div className="flex animate-cool-down items-center gap-1.5 text-caption text-text-tertiary">
            <span className="text-success" aria-hidden>✓</span>
            Confirmed by {confirmedBy}
            {confirmedAt && <span className="text-text-tertiary">· {confirmedAt}</span>}
          </div>
        )}
        {advisory && confidence && <ConfidenceBand level={confidence} />}
      </div>
      <div className="text-body-md font-medium text-text-primary">{title}</div>
      <div className="mt-1 text-body-md text-text-secondary">{children}</div>
      {actions && <div className="mt-3 flex items-center gap-2">{actions}</div>}
    </div>
  );
}
